// src/pages/project/payment/ContractMilestoneEditorModal.tsx
import React, { useEffect, useState } from 'react';

import { toProjectDateOnly, toUtcDeadline } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

import { v4 as uuidv4 } from 'uuid';
import { ContractMilestone, PaymentStatus } from 'src/types/contractMilestone.type';

type CriteriaItem = {
    id: string;
    text: string;
    done?: boolean;
};

export type MilestoneDraft = Omit<ContractMilestone, 'id' | 'createdAt' | 'updatedAt' | 'payments'> & {
    id?: string;
    createdAt?: string | null;
    updatedAt?: string | null;

    // ✅ UI 레거시 유지용 (DB에는 저장 안됨)
    actualAmount?: number | null;
    eventDate?: string | null;
};

export default function ContractMilestoneEditorModal({
    open,
    onClose,
    value,
    onSave,
    onDelete,
    canEdit,
}: {
    open: boolean;
    onClose: () => void;
    value?: MilestoneDraft | null; // null = 새로 만들기
    onSave: (next: MilestoneDraft) => void;
    onDelete?: (id: string) => void; // 기존 항목 편집일 때만 사용
    canEdit?: boolean;
}) {
    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    const [draft, setDraft] = useState<MilestoneDraft | null>(null);

    useEffect(() => {
        if (!open) return;

        if (!value) {
            setDraft(null);
            return;
        }

        const due = (value as any).dueDate;
        const dueDateOnly = typeof due === 'string' && due.includes('T') ? toProjectDateOnly(due, zone) : typeof due === 'string' ? due : null;
        
        setDraft({ ...value, dueDate: dueDateOnly } as any);
    }, [value, open, zone]);

    if (!open || !draft) return null;

    const onField = <K extends keyof MilestoneDraft>(k: K, v: MilestoneDraft[K]) =>
        setDraft((prev) => (prev ? ({ ...prev, [k]: v } as MilestoneDraft) : prev));

    const isNew = !draft.id;

    const handleDelete = () => {
        if (!onDelete || !draft?.id) return;
        // eslint-disable-next-line no-restricted-globals
        if (!confirm('이 마일스톤을 삭제할까요?')) return;
        onDelete(draft.id);
    };

    // ----------- Completion Criteria (Todo) helpers -----------
    const criteria: CriteriaItem[] = draft.completionCriteria ?? [];
    const addCriteria = () => {
        const next = [...criteria, { id: uuidv4(), text: '', done: false }];
        onField('completionCriteria', next);
    };
    const updateCriteria = (id: string, patch: Partial<CriteriaItem>) => {
        const next = criteria.map((c) => (c.id === id ? { ...c, ...patch } : c));
        onField('completionCriteria', next);
    };
    const removeCriteria = (id: string) => {
        const next = criteria.filter((c) => c.id !== id);
        onField('completionCriteria', next);
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black/30" onClick={onClose}>
            <div
                className="mx-auto mt-24 w-full max-w-xl rounded-2xl bg-white border shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-3 border-b text-sm font-medium">
                    {isNew ? 'Milestone Payment 추가' : 'Milestone Payment 편집'}
                </div>

                <div className="p-3 space-y-3 text-sm">
                    {/* 기본 정보 */}
                    {/* <div className="grid grid-cols-2 gap-2">
                        <label className="space-y-1">
                            <div className="text-xs text-gray-500">Seq</div>
                            <input
                                type="number"
                                className="w-full rounded border px-2 py-1"
                                value={draft.seq ?? 1}
                                onChange={(e) => onField('seq', Number(e.target.value))}
                                disabled={!canEdit}
                            />
                        </label>
                        <label className="space-y-1">
                            <div className="text-xs text-gray-500">Percent(%)</div>
                            <input
                                type="number"
                                className="w-full rounded border px-2 py-1"
                                value={draft.percent ?? ''}
                                onChange={(e) =>
                                    onField('percent', e.target.value === '' ? null : Number(e.target.value))
                                }
                                disabled={!canEdit}
                            />
                        </label>
                        </div> */}
                        
                                            <label className="space-y-1 block">
                                                <div className="text-xs text-gray-500">Milestone Activity</div>
                                                <input
                                                    className="w-full rounded border px-2 py-1"
                                                    value={draft.label}
                                                    onChange={(e) => onField('label', e.target.value)}
                                                    disabled={!canEdit}
                                                />
                                            </label>

                                        <div className="grid grid-cols-2 gap-2">
                                                <label className="space-y-1">
                            <div className="text-xs text-gray-500">Percent(%)</div>
                            <input
                                type="number"
                                className="w-full rounded border px-2 py-1"
                                value={draft.percent ?? ''}
                                onChange={(e) =>
                                    onField('percent', e.target.value === '' ? null : Number(e.target.value))
                                }
                                disabled={!canEdit}
                            />
                        </label>
                        <label className="space-y-1">
                            <div className="text-xs text-gray-500">Plan Amount</div>
                            <input
                                type="number"
                                className="w-full rounded border px-2 py-1"
                                value={draft.amountPlan ?? ''}
                                onChange={(e) =>
                                    onField('amountPlan', e.target.value === '' ? null : Number(e.target.value))
                                }
                                disabled={!canEdit}
                            />
                        </label>
                        {/* <label className="space-y-1">
                            <div className="text-xs text-gray-500">Actual Amount</div>
                            <input
                                type="number"
                                className="w-full rounded border px-2 py-1"
                                value={draft.actualAmount ?? ''}
                                onChange={(e) =>
                                    onField('actualAmount', e.target.value === '' ? null : Number(e.target.value))
                                }
                                disabled={!canEdit}
                            />
                        </label> */}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <label className="space-y-1">
                            <div className="text-xs text-gray-500">Due Date</div>
                            <input
                                type="date"
                                className="w-full rounded border px-2 py-1"
                                value={draft.dueDate ?? ''}
                                onChange={(e) => onField('dueDate', e.target.value || null)}
                                disabled={!canEdit}
                            />
                        </label>
                        <label className="space-y-1">
                            <div className="text-xs text-gray-500">Status</div>
                            <select
                                className="w-full rounded border px-2 py-1"
                                value={draft.status}
                                onChange={(e) => onField('status', e.target.value as PaymentStatus)}
                                disabled={!canEdit}
                            >
                                {['PLANNED', 'READY', 'INVOICED', 'APPROVED', 'PAID', 'REJECTED', 'DELAYED'].map(
                                    (s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ),
                                )}
                            </select>
                        </label>
                    </div>



                    {/* <div className="grid grid-cols-2 gap-2">
                        <label className="space-y-1">
                            <div className="text-xs text-gray-500">Event Date</div>
                            <input
                                type="date"
                                className="w-full rounded border px-2 py-1"
                                value={draft.eventDate ?? ''}
                                onChange={(e) => onField('eventDate', e.target.value || null)}
                                disabled={!canEdit}
                            />
                        </label>
                        <div className="space-y-1">
                            <div className="text-xs text-gray-500">Linked WBS (ids)</div>
                            <input
                                className="w-full rounded border px-2 py-1"
                                value={(draft.linkedWbsIds ?? []).join(', ')}
                                onChange={(e) =>
                                    onField(
                                        'linkedWbsIds',
                                        e.target.value
                                            .split(',')
                                            .map((s) => s.trim())
                                            .filter(Boolean)
                                            .map((n) => Number(n)),
                                    )
                                }
                                disabled={!canEdit}
                                placeholder="예: 12, 25"
                            />
                        </div>
                    </div> */}

                    {/* ✅ Completion Criteria (Todo List) */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="text-xs text-gray-500">Completion Criteria (Todo)</div>
                            {canEdit && (
                                <button
                                    type="button"
                                    className="text-xs rounded border px-2 py-0.5 bg-white hover:bg-gray-50"
                                    onClick={addCriteria}
                                >
                                    + Add Item
                                </button>
                            )}
                        </div>
                        <div className="rounded border divide-y">
                            {criteria.length === 0 && (
                                <div className="px-3 py-2 text-xs text-gray-400">항목이 없습니다.</div>
                            )}
                            {criteria.map((c) => (
                                <div key={c.id} className="px-3 py-2 flex items-start gap-2">
                                    <input
                                        type="checkbox"
                                        className="mt-1 accent-gray-700"
                                        checked={!!c.done}
                                        onChange={(e) => updateCriteria(c.id, { done: e.target.checked })}
                                        disabled={!canEdit}
                                        title="Done"
                                    />
                                    <input
                                        className="flex-1 rounded border px-2 py-1 text-sm"
                                        value={c.text}
                                        onChange={(e) => updateCriteria(c.id, { text: e.target.value })}
                                        disabled={!canEdit}
                                        placeholder="완료 판정 기준을 입력하세요"
                                    />
                                    {canEdit && (
                                        <button
                                            className="text-xs rounded border px-2 py-1 hover:bg-rose-50 border-rose-200 text-rose-700"
                                            onClick={() => removeCriteria(c.id)}
                                            title="삭제"
                                        >
                                            삭제
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ✅ Note */}
                    <label className="space-y-1 block">
                        <div className="text-xs text-gray-500">Note</div>
                        <textarea
                            className="w-full rounded border px-2 py-1"
                            rows={4}
                            value={draft.note ?? ''}
                            onChange={(e) => onField('note', e.target.value || null)}
                            disabled={!canEdit}
                            placeholder="진행 상황/특이사항 등"
                        />
                    </label>
                </div>

                <div className="p-3 border-t flex items-center justify-between">
                    <div>
                        {/* 기존 항목이고 권한 있을 때만 삭제 노출 */}
                        {canEdit && draft.id && onDelete && (
                            <button
                                className="rounded border px-3 py-1.5 text-sm bg-white hover:bg-rose-50 border-rose-200 text-rose-700"
                                onClick={handleDelete}
                            >
                                Delete
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <button className="rounded border px-3 py-1.5 text-sm" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            className="rounded bg-gray-900 text-white px-3 py-1.5 text-sm disabled:opacity-50"
                            onClick={() => {
                                const next = {
                                    ...draft,
                                    dueDate: draft.dueDate ? toUtcDeadline(draft.dueDate, zone) : null,
                                }
                                onSave(next);
                                onClose();
                            }}
                            disabled={!canEdit}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
